"use client"

import { Heading } from "@/components/ui/Heading"
import { useDictionary } from "@/core/hooks/use-dictionary"
import { cn } from "@/core/lib/utils"
import { MainShopProduct } from "@/core/models/shop-product-model"
import { useState } from "react"


type TabKey = "details" | "dimensions" | "description"

export default function ProductDetailTabs({ product }: { product: MainShopProduct }) {

    const { dictionary } = useDictionary()
    const [activeTab, setActiveTab] = useState<TabKey>("details")

    const validMetaFields = product.meta_fields?.filter((item) => item.value != null && item.value !== "")

    const hasDimensions =
        (Number(product.fabric_area) !== 0 && product.fabric_area) ||
        (Number(product.weight) !== 0 && product.weight) ||
        product.overalsize

    const tabs: { key: TabKey; label: string }[] = []
    if (validMetaFields?.length > 0) { tabs.push({ key: "details", label: dictionary.ui.product.details.title }) }
    if (hasDimensions) tabs.push({ key: "dimensions", label: dictionary.forms.dimensions })
    if (product.content) tabs.push({ key: "description", label: dictionary.forms.description })

    const effectiveTab = tabs.find((t) => t.key === activeTab) ? activeTab : tabs[0]?.key

    if (tabs.length === 0) return null

    return (
        <div className="flex flex-col">
            {/* Tab buttons */}
            <div className="flex gap-0 border-b border-border">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={cn(
                            "px-6 py-4 text-sm transition-all duration-300 relative",
                            effectiveTab === tab.key ? "text-primary-900" : "text-primary-600 hover:text-primary-900",
                        )}
                    >
                        {tab.label}
                        {effectiveTab === tab.key && (
                            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-900 transition-all duration-300" />
                        )}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            <div className="py-8 animate-fade-in">
                {effectiveTab === "details" && validMetaFields.length > 0 && (
                    <div className="flex flex-col gap-0">
                        {validMetaFields.map((item, index) => (
                            <div key={index} className="flex items-baseline justify-between py-3.5 border-b border-border/40">
                                <span className="text-xs text-primary-600">{item.label}</span>
                                <span className="text-sm text-primary-900 text-right">{item.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {effectiveTab === "dimensions" && hasDimensions && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {Number(product.fabric_area) !== 0 && product.fabric_area && (
                            <div className="flex flex-col gap-3 p-5 bg-primary-100/50 border border-primary-300/30">
                                <span className="text-xs text-primary-600">{dictionary.forms.fabric_area}</span>
                                <Heading level={2}>{product.fabric_area}</Heading>
                            </div>
                        )}
                        {Number(product.weight) !== 0 && product.weight && (
                            <div className="flex flex-col gap-3 p-5 bg-primary-100/50 border border-primary-300/30">
                                <span className="text-xs text-primary-600">{dictionary.forms.weight}</span>
                                <Heading level={2}>{product.weight}</Heading>
                            </div>
                        )}
                        {product.overalsize && (
                            <div className="flex flex-col gap-3 p-5 bg-primary-100/50 border border-primary-300/30">
                                <span className="text-xs text-primary-600">{dictionary.forms.overalsize}</span>
                                <Heading level={2}>{product.overalsize}</Heading>
                            </div>
                        )}
                    </div>
                )}

                {effectiveTab === "description" && product.content && (
                    <div
                        className="text-sm text-primary-600 leading-relaxed prose prose-sm"
                        dangerouslySetInnerHTML={{ __html: product.content }}
                    />
                )}
            </div>
        </div>
    )
}
